import type { RefObject } from "react";
import { useEffect } from "react";

const defaultIgnoreRefs: Array<RefObject<HTMLElement | null>> = [];

/**
 * Invokes `handler` when pointer or focus leaves `rootRef` without entering `ignoreRefs`.
 */
export function useOnClickOutside(
  rootRef: RefObject<HTMLElement | null>,
  handler: () => void,
  open: boolean,
  ignoreRefs: Array<RefObject<HTMLElement | null>> = defaultIgnoreRefs,
): void {
  useEffect(() => {
    if (!open) {
      return;
    }

    function isInside(ref: RefObject<HTMLElement | null>, target: EventTarget | null): boolean {
      if (!target || !(target instanceof Node) || !ref.current) {
        return false;
      }
      return ref.current.contains(target);
    }

    function shouldIgnore(target: EventTarget | null): boolean {
      if (isInside(rootRef, target)) {
        return true;
      }
      return ignoreRefs.some((r) => isInside(r, target));
    }

    function onPointerDown(e: MouseEvent | TouchEvent): void {
      if (shouldIgnore(e.target)) {
        return;
      }
      handler();
    }

    function onFocusIn(e: FocusEvent): void {
      if (shouldIgnore(e.target)) {
        return;
      }
      handler();
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, rootRef, handler, ignoreRefs]);
}
